import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Row,
  Col,
  Divider,
  message
} from 'antd';
import ImageUpload from '../Common/ImageUpload';
import { uploadProductImage, deleteUploadedFile, getImageUrl } from '../../services/uploadService';
import { serviceProduits } from '../../services/productsService';
import api from '../../services/api';

const { Option } = Select;
const { TextArea } = Input;

interface ProductFormProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (values: any) => void;
  initialValues?: any;
  loading?: boolean;
}

// Interface pour les données du formulaire (champs en anglais)
interface ProductFormValues {
  name: string;
  reference: string;
  description: string;
  price: number;
  base_price: number;
  stock: number;
  seuil_alerte: number;
  marque_id?: number;
  categorie_id: number;
  active: boolean;
  offer_id: number | null;
  specifications: {
    puissance?: string;
    efficacite?: string;
    refrigerant?: string;
    debit?: string;
    niveau_sonore?: string;
    duree?: string;
    garantie?: string;
    inclus?: string;
    dimensions?: string;
    duree_vie?: string;
  };
  images: string[];
}

const ProductForm: React.FC<ProductFormProps> = ({
  visible,
  onCancel,
  onSubmit,
  initialValues,
  loading = false
}) => {
  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);
  const [currentImage, setCurrentImage] = useState<string | undefined>(undefined);
  const [uploadedImageFilename, setUploadedImageFilename] = useState<string | undefined>(undefined);
  const [productId, setProductId] = useState<number | undefined>(undefined);
  const [categories, setCategories] = useState<any[]>([]);
  const [marques, setMarques] = useState<any[]>([]);
  const [offers, setOffers] = useState<any[]>([]);
  const [loadingData, setLoadingData] = useState(false);
  const [selectedOfferId, setSelectedOfferId] = useState<number | null>(null);
  const [basePrice, setBasePrice] = useState<number>(0);

  // Calculer le prix de l'offre sélectionnée
  const selectedOfferPrice = selectedOfferId 
    ? (offers.find(o => o.id === selectedOfferId)?.price || 0) 
    : 0;

  // Mettre à jour le prix total quand l'offre change
  const handleOfferChange = (offerId: number | null) => {
    setSelectedOfferId(offerId);
    const currentBasePrice = parseFloat(form.getFieldValue('base_price')) || basePrice || 0;
    const offer = offerId ? offers.find(o => o.id === offerId) : null;
    const offerPrice = offer ? parseFloat(offer.price) || 0 : 0;
    const totalPrice = currentBasePrice + offerPrice;
    form.setFieldValue('price', totalPrice);
  };

  // Mettre à jour le prix quand le prix de base change
  const handleBasePriceChange = (value: number | null) => {
    const newBasePrice = value || 0;
    setBasePrice(newBasePrice);
    const offer = selectedOfferId ? offers.find(o => o.id === selectedOfferId) : null;
    const offerPrice = offer ? parseFloat(offer.price) || 0 : 0;
    const totalPrice = newBasePrice + offerPrice;
    form.setFieldValue('price', totalPrice);
  };

  useEffect(() => {
    if (visible) {
      form.resetFields();
      
      if (initialValues && Object.keys(initialValues).length > 0) {
        setIsEditing(true);
        // Convertir les noms de champs français vers anglais pour le formulaire
        const formValues = {
          ...initialValues,
          name: initialValues.nom || '',
          price: initialValues.prix != null ? Number(initialValues.prix) : 0,
          stock: initialValues.quantite_stock != null ? Number(initialValues.quantite_stock) : 0,
          active: initialValues.actif ?? true,
          offer_id: initialValues.offer_id || null,
          base_price: initialValues.base_price || initialValues.prix || 0,
          seuil_alerte: initialValues.seuil_alerte != null ? Number(initialValues.seuil_alerte) : 5,
          marque_id: initialValues.marque_id ? Number(initialValues.marque_id) : undefined,
          categorie_id: initialValues.categorie_id ? Number(initialValues.categorie_id) : undefined,
          specifications: initialValues.specifications || {}
        };
        
        console.log('Setting form values:', formValues);
        form.setFieldsValue(formValues);
        
        // Définir l'image actuelle et l'ID du produit
        setProductId(initialValues.id);
        // Charger l'image depuis le tableau images (préfixer avec l'URL de l'API)
        if (initialValues.images && initialValues.images.length > 0) {
          setCurrentImage(getImageUrl(initialValues.images[0]));
        } else if (initialValues.image_url) {
          setCurrentImage(getImageUrl(initialValues.image_url));
        } else {
          setCurrentImage(undefined);
        }
        setUploadedImageFilename(undefined);
      } else {
        setIsEditing(false);
        const defaultValues = {
          active: true,
          offer_id: null,
          seuil_alerte: 5,
          stock: 0,
          price: 0,
          base_price: 0,
          specifications: {}
        };
        console.log('Setting default values:', defaultValues);
        form.setFieldsValue(defaultValues);
        setCurrentImage(undefined);
        setProductId(undefined);
        setUploadedImageFilename(undefined);
        setSelectedOfferId(null);
        setBasePrice(0);
      }
    }
  }, [visible, initialValues, form]);

  // Initialiser l'offre sélectionnée quand les données initiales changent
  useEffect(() => {
    if (initialValues?.offer_id) {
      setSelectedOfferId(initialValues.offer_id);
    }
    if (initialValues?.base_price) {
      setBasePrice(initialValues.base_price);
    } else if (initialValues?.prix) {
      setBasePrice(initialValues.prix);
    }
  }, [initialValues]);

  // Charger les catégories, marques et offres depuis l'API
  useEffect(() => {
    const fetchData = async () => {
      setLoadingData(true);
      try {
        const [categoriesRes, brandsRes, offersRes] = await Promise.all([
          api.get('/categories'),
          api.get('/brands'),
          api.get('/maintenance-offers')
        ]);
        
        setCategories(categoriesRes.data.data || []);
        setMarques(brandsRes.data.data || []);
        setOffers(offersRes.data.data || offersRes.data || []);
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
        message.error('Impossible de charger les données');
        // Fallback vers les données par défaut en cas d'erreur
        setCategories([
          { id: 1, nom: 'Climatisation' },
          { id: 2, nom: 'Chauffage' },
          { id: 3, nom: 'Ventilation' },
          { id: 4, nom: 'Service' },
          { id: 5, nom: 'Filtration'}
        ]);
        setMarques([
          { id: 1, nom: 'Daikin' },
          { id: 2, nom: 'Mitsubishi' },
          { id: 3, nom: 'Samsung' },
          { id: 4, nom: 'LK' },
          { id: 5, nom: 'Carrier' }
        ]);
        setOffers([]);
      } finally {
        setLoadingData(false);
      }
    };
    
    if (visible) {
      fetchData();
    }
  }, [visible]);

  const handleImageUpload = async (file: File) => {
    try {
      // Upload l'image même sans productId (le backend l'accepte)
      const result = await uploadProductImage(file, productId || 0);
      setCurrentImage(result.url);
      
      // Si on est en mode édition, mettre à jour le produit directement
      if (productId) {
        await serviceProduits.mettreAJourProduit(productId, {
          images: [result.url]
        });
        message.success('Image uploadée avec succès');
      } else {
        // En mode création, stocker le filename pour l'utiliser lors de la création
        setUploadedImageFilename(result.filename || result.url.split('/').pop());
        message.success('Image uploadée. Elle sera associée au produit lors de la création.');
      }
      
      return result;
    } catch (error) {
      console.error('Erreur upload image:', error);
      message.error('Erreur lors de l\'upload de l\'image');
      throw error;
    }
  };

  const handleImageDelete = async () => {
    if (currentImage) {
      const filename = currentImage.split('/').pop();
      if (filename) {
        await deleteUploadedFile('products', filename);
        setCurrentImage(undefined);
        setUploadedImageFilename(undefined);
        message.success('Image supprimée');
      }
    }
  };

  const handleFormSubmit = async (values: ProductFormValues) => {
    try {
      // Construire l'URL complète de l'image si on a un filename
      let imageUrl = currentImage;
      if (uploadedImageFilename && !currentImage?.startsWith('http')) {
        imageUrl = `/uploads/products/${uploadedImageFilename}`;
      }

      // Convertir les noms de champs anglais vers français pour l'API
      const apiValues = {
        ...values,
        nom: values.name || '',
        prix: values.price ? Number(values.price) : 0,
        quantite_stock: values.stock ? Number(values.stock) : 0,
        actif: values.active ?? true,
        offer_id: values.offer_id || null,
        base_price: values.base_price || 0,
        marque_id: values.marque_id ? Number(values.marque_id) : null,
        categorie_id: values.categorie_id ? Number(values.categorie_id) : null,
        seuil_alerte: values.seuil_alerte || 5,
        images: imageUrl ? [imageUrl] : [],
        specifications: values.specifications || {}
      };

      console.log('Soumission du formulaire avec les valeurs:', apiValues);
      onSubmit(apiValues);
    } catch (error) {
      console.error('Erreur lors de la soumission du formulaire:', error);
      message.error('Une erreur est survenue lors de la soumission du formulaire');
    }
  };

  return (
    <Modal
      title={isEditing ? 'Modifier le produit' : 'Nouveau produit'}
      open={visible}
      onCancel={onCancel}
      onOk={() => form.submit()}
      confirmLoading={loading}
      width={800}
      okText={isEditing ? 'Mettre à jour' : 'Créer'}
      cancelText="Annuler"
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFormSubmit}
        initialValues={{
          active: true,
          offer_id: null,
          base_price: 0,
          seuil_alerte: 5,
          stock: 0,
          price: 0,
          specifications: {}
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="name"
              label="Nom du produit"
              rules={[{ required: true, message: 'Veuillez saisir le nom du produit' }]}
            >
              <Input placeholder="Nom du produit" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="reference"
              label="Référence"
              rules={[{ required: true, message: 'Veuillez saisir la référence' }]}
            >
              <Input placeholder="Référence du produit" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="description"
          label="Description"
          rules={[{ required: true, message: 'Veuillez saisir une description' }]}
        >
          <TextArea rows={3} placeholder="Description détaillée du produit" />
        </Form.Item>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="base_price"
              label="Prix équipement (FCFA)"
              rules={[{ required: true, message: 'Veuillez saisir le prix' }]}
            >
              <InputNumber<number>
                style={{ width: '100%' }}
                min={0}
                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => {
                  const num = value ? value.toString().replace(/,/g, '') : '0';
                  return parseFloat(num) || 0;
                }}
                onChange={handleBasePriceChange}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="offer_id"
              label="Offre associée"
            >
              <Select
                placeholder="Sélectionner une offre"
                loading={loadingData}
                allowClear
                onChange={handleOfferChange}
              >
                {offers.filter(o => o.isActive !== false).map(offer => (
                  <Option key={offer.id} value={offer.id}>
                    {offer.title} (+{(parseFloat(offer.price) || 0).toLocaleString('fr-FR')} FCFA)
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="price"
              label="Prix total (FCFA)"
            >
              <InputNumber<number>
                style={{ width: '100%', fontWeight: 'bold' }}
                min={0}
                disabled
                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="stock"
              label="Quantité en stock"
              rules={[{ required: true, message: 'Veuillez saisir la quantité en stock' }]}
            >
              <InputNumber<number> style={{ width: '100%' }} min={0} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="seuil_alerte"
              label="Seuil d'alerte"
              rules={[{ required: true, message: "Veuillez saisir le seuil d'alerte" }]}
            >
              <InputNumber<number> style={{ width: '100%' }} min={0} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="marque_id"
              label="Marque"
              rules={[{ required: true, message: 'Veuillez sélectionner une marque' }]}
            >
              <Select placeholder="Sélectionner une marque" loading={loadingData}>
                {marques.map(marque => (
                  <Option key={marque.id} value={marque.id}>{marque.nom}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="categorie_id"
              label="Catégorie"
              rules={[{ required: true, message: 'Veuillez sélectionner une catégorie' }]}
            >
              <Select placeholder="Sélectionner une catégorie" loading={loadingData}>
                {categories.map(cat => (
                  <Option key={cat.id} value={cat.id}>{cat.nom}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="active"
              label="Statut"
              valuePropName="checked"
            >
              <Switch
                checkedChildren="Actif"
                unCheckedChildren="Inactif"
                defaultChecked
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left">Spécifications techniques</Divider>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name={['specifications', 'puissance']} label="Puissance">
              <Input placeholder="Ex: 12000 BTU" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name={['specifications', 'efficacite']} label="Efficacité énergétique">
              <Input placeholder="Ex: A++" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name={['specifications', 'refrigerant']} label="Réfrigérant">
              <Input placeholder="Ex: R32" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name={['specifications', 'debit']} label="Débit d'air">
              <Input placeholder="Ex: 600 m³/h" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name={['specifications', 'niveau_sonore']} label="Niveau sonore">
              <Input placeholder="Ex: 22 dB(A)" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name={['specifications', 'garantie']} label="Garantie">
              <Input placeholder="Ex: 2 ans" />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left">Image du produit</Divider>
        
        <ImageUpload
          currentImage={currentImage}
          onUpload={handleImageUpload}
          onDelete={handleImageDelete}
          label="Image du produit"
          type="product"
        />
        
        {!isEditing && uploadedImageFilename && (
          <p style={{ color: '#52c41a', fontSize: '12px', marginTop: '8px' }}>
            ✓ Image prête. Elle sera associée au produit lors de la création.
          </p>
        )}
      </Form>
    </Modal>
  );
};

export default ProductForm;
